import { useRef, useEffect } from "react";
import { Bold, Italic, Underline, List, ListOrdered, Link2, RemoveFormatting, Image as ImageIcon, Heading1, Heading2, Quote } from "lucide-react";
import { toast } from "sonner";

const MAX_IMG_BYTES = 1_500_000; // 1.5 MB por imagem (base64 inline)

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

async function insertImageFromFile(file) {
  if (!file || !file.type?.startsWith("image/")) return null;
  if (file.size > MAX_IMG_BYTES) {
    toast.error(`Imagem demasiado grande (max ${Math.round(MAX_IMG_BYTES / 1000)} KB).`);
    return null;
  }
  return await fileToDataUrl(file);
}

export function RichText({ value, onChange, testid, minHeight = 150, maxHeight = 320 }) {
  const ref = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (ref.current && document.activeElement !== ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
  }, [value]);

  const emit = () => onChange(ref.current?.innerHTML || "");
  const exec = (cmd, arg = null) => { document.execCommand(cmd, false, arg); ref.current?.focus(); emit(); };
  const addLink = () => { const url = window.prompt("URL do link (https://...):"); if (url) exec("createLink", url); };

  const insertImageDataUrl = (dataUrl) => {
    ref.current?.focus();
    document.execCommand("insertHTML", false, `<img src="${dataUrl}" style="max-width:100%;height:auto;border-radius:6px;margin:6px 0" alt="" />`);
    emit();
  };

  const onPaste = async (e) => {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.type?.startsWith("image/")) {
        e.preventDefault();
        const file = it.getAsFile();
        const url = await insertImageFromFile(file);
        if (url) insertImageDataUrl(url);
        return;
      }
    }
    // texto: mantem o comportamento default do execCommand, mas sem formatacao residual
  };

  const onDrop = async (e) => {
    const files = Array.from(e.dataTransfer?.files || []);
    const img = files.find((f) => f.type?.startsWith("image/"));
    if (img) {
      e.preventDefault();
      const url = await insertImageFromFile(img);
      if (url) insertImageDataUrl(url);
    }
  };

  const onFilePick = async (e) => {
    const f = e.target.files?.[0];
    if (f) {
      const url = await insertImageFromFile(f);
      if (url) insertImageDataUrl(url);
    }
    e.target.value = "";
  };

  const TBtn = ({ onClick, children, tid, title }) => (
    <button type="button" title={title} data-testid={tid}
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      className="p-1.5 hover:bg-tint rounded text-navy transition-colors">{children}</button>
  );

  return (
    <div className="border border-tint rounded-lg overflow-hidden">
      <div className="flex items-center gap-0.5 border-b border-tint bg-tint/40 px-1 py-1 flex-wrap">
        <TBtn tid="rt-h1" title="Titulo grande" onClick={() => exec("formatBlock", "H2")}><Heading1 size={15} /></TBtn>
        <TBtn tid="rt-h2" title="Subtitulo" onClick={() => exec("formatBlock", "H3")}><Heading2 size={15} /></TBtn>
        <span className="w-px h-4 bg-tint mx-1" />
        <TBtn tid="rt-bold" title="Negrito" onClick={() => exec("bold")}><Bold size={15} /></TBtn>
        <TBtn tid="rt-italic" title="Italico" onClick={() => exec("italic")}><Italic size={15} /></TBtn>
        <TBtn tid="rt-underline" title="Sublinhado" onClick={() => exec("underline")}><Underline size={15} /></TBtn>
        <span className="w-px h-4 bg-tint mx-1" />
        <TBtn tid="rt-ul" title="Lista" onClick={() => exec("insertUnorderedList")}><List size={15} /></TBtn>
        <TBtn tid="rt-ol" title="Lista numerada" onClick={() => exec("insertOrderedList")}><ListOrdered size={15} /></TBtn>
        <TBtn tid="rt-quote" title="Citacao" onClick={() => exec("formatBlock", "BLOCKQUOTE")}><Quote size={15} /></TBtn>
        <span className="w-px h-4 bg-tint mx-1" />
        <TBtn tid="rt-link" title="Link" onClick={addLink}><Link2 size={15} /></TBtn>
        <TBtn tid="rt-image" title="Inserir imagem (pode tambem colar ou arrastar)" onClick={() => fileRef.current?.click()}><ImageIcon size={15} /></TBtn>
        <input ref={fileRef} type="file" accept="image/*" onChange={onFilePick} className="hidden" data-testid="rt-image-input" />
        <span className="w-px h-4 bg-tint mx-1" />
        <TBtn tid="rt-clear" title="Limpar formatacao" onClick={() => exec("removeFormat")}><RemoveFormatting size={15} /></TBtn>
      </div>
      <div ref={ref} contentEditable data-testid={testid}
        onInput={emit} onBlur={emit} onPaste={onPaste} onDrop={onDrop} onDragOver={(e) => e.preventDefault()}
        suppressContentEditableWarning
        style={{ minHeight, maxHeight }}
        className="rte-content overflow-y-auto px-3 py-2 text-sm text-navy focus:outline-none leading-relaxed" />
      <style>{`
        .rte-content ul { list-style: disc; margin: 8px 0; padding-left: 24px; }
        .rte-content ol { list-style: decimal; margin: 8px 0; padding-left: 24px; }
        .rte-content li { margin: 2px 0; }
        .rte-content h1 { font-size: 24px; font-weight: bold; margin: 12px 0 6px 0; }
        .rte-content h2 { font-size: 20px; font-weight: bold; margin: 10px 0 5px 0; }
        .rte-content h3 { font-size: 16px; font-weight: bold; margin: 8px 0 4px 0; }
        .rte-content blockquote { border-left: 3px solid #cbd5e1; padding-left: 12px; color: #475569; margin: 8px 0; }
      `}</style>
    </div>
  );
}
